import { NgClass, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Dayjs } from 'dayjs';
import { IDatatableDueDateItem } from '../../../pages/items/item-view/items-datatable/items-datatable.interface';
import { EDateUnits } from '../../../utilities/date-utilities.interface';
import { DateUtilitiesService } from '../../../utilities/date-utilities.service';

@Component({
  imports: [MatIconModule, NgIf, NgClass, MatTooltipModule, TranslatePipe, MatChipsModule],
  selector: 'app-due-date-chip',
  styleUrls: ['./due-date-chip.component.scss'],
  templateUrl: './due-date-chip.component.html',
})
export class DueDateChipComponent implements OnInit {
  @Input() public dueDate: IDatatableDueDateItem = { formatted: '', original: '' };
  @Input({ required: true }) public doIgnoreDueDateCalculation!: boolean;
  @Input() public strikeCount = 0;

  protected isOverDue = false;
  protected isDueSoon = false;
  protected overDueTimeString = '';
  protected icon = 'schedule';

  constructor(private readonly translateService: TranslateService) {}

  public get isStrikeCount(): boolean {
    return this.strikeCount >= 3;
  }

  public ngOnInit(): void {
    this.calculateOverDue();
  }

  protected onOverDueMouseEnter(): void {
    this.calculateOverDue();
  }

  private calculateOverDue(): void {
    if (!this.dueDate?.original) {
      return;
    }

    const currentDate: Dayjs = DateUtilitiesService.getNowInUTC();
    const dueDate: Dayjs = DateUtilitiesService.buildDayjsFromISO8601DateString(this.dueDate.original);

    this.isOverDue = !this.doIgnoreDueDateCalculation && DateUtilitiesService.isDateLaterThenDate(currentDate, dueDate);

    if (!this.isOverDue && !this.doIgnoreDueDateCalculation) {
      const daysDiff: number = dueDate.diff(currentDate, 'day', true);
      this.isDueSoon = daysDiff > 0 && daysDiff <= 7;
    } else {
      this.isDueSoon = false;
    }

    this.setIcon();

    const timeDifferenceInMillisecond: number = DateUtilitiesService.calculateTimeDifferenceInMilliseconds(
      currentDate,
      dueDate,
    );

    const { days, hours, minutes, seconds } = DateUtilitiesService.getTimeUnitsInMilliseconds(
      timeDifferenceInMillisecond,
      [EDateUnits.days, EDateUnits.hours, EDateUnits.minutes, EDateUnits.seconds],
    );

    const dayString: string = this.formatTimeUnit(days, 'field.day', 'field.days');
    const hourString: string = this.formatTimeUnit(hours, 'field.hour', 'field.hours');
    const minuteString: string = this.formatTimeUnit(minutes, 'field.minute', 'field.minutes');
    const secondString: string = this.getSecondsString(days, hours, minutes, seconds);

    this.overDueTimeString = `
      ${this.translateService.instant('page.items.label.overdue')}:
      ${dayString} ${hourString} ${minuteString} ${secondString}
    `;
  }

  private setIcon(): void {
    if (this.isOverDue) {
      this.icon = 'error_outline';
    } else if (this.isDueSoon) {
      this.icon = 'warning';
    } else {
      this.icon = 'schedule';
    }
  }

  private formatTimeUnit(value: number, singularKey: string, pluralKey: string): string {
    return value > 0
      ? `${value} ${value > 1 ? this.translateService.instant(pluralKey) : this.translateService.instant(singularKey)}`
      : '';
  }

  private getSecondsString(days: number, hours: number, minutes: number, seconds: number): string {
    if (days === 0 && hours === 0 && minutes === 0 && seconds > 0) {
      return `
        ${seconds}
        ${seconds > 1 ? this.translateService.instant('field.seconds') : this.translateService.instant('field.second')}
      `;
    }

    return '';
  }
}
